import { X } from '@phosphor-icons/react';
import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, FC, KeyboardEvent, MouseEvent } from 'react';

import { GAME_IMAGES } from '@/app/agent/media.ts';
import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { useSurfaceAction } from '@/app/lib/a2ui/surface-context.ts';

const MAX_BOMBS = 4;
const BOMB_INDICATORS = 4;
const SPRITE_COLUMNS = 6;
const SPRITE_ROWS = 5;
const SPRITE_ATLAS_SIZE = 1024;
// The circular artwork is padded on an uneven grid inside the 1024px atlas.
// Aligning the viewport to these measured centers keeps each circle whole and
// prevents a neighboring circle edge from entering the crop.
const SPRITE_CENTER_X = [94, 261.5, 428, 596, 763, 931.5];
const SPRITE_CENTER_Y = [106, 312.5, 518.5, 726.5, 930];
const MIN_COINS = 5;
const MAX_COINS = 10;
const BOMB_CHANCE = 0.035;

type GameLevel = 1 | 2 | 3;
type LevelConfig = {
  id: GameLevel;
  targetCoins: number;
  coinCountMultiplier: number;
  bombFrequencyDivider: number;
};

const LEVEL_CONFIGS: Record<GameLevel, LevelConfig> = {
  1: { id: 1, targetCoins: 70, coinCountMultiplier: 1, bombFrequencyDivider: 1 },
  2: { id: 2, targetCoins: 100, coinCountMultiplier: 1.5, bombFrequencyDivider: 1.5 },
  3: { id: 3, targetCoins: 150, coinCountMultiplier: 2, bombFrequencyDivider: 2 },
};

const MIN_COIN_DISTANCE = 30;
const MIN_COIN_SEPARATION = 20;
const COLLECTION_RADIUS_X = 15;
const COLLECTION_RADIUS_Y = 24;
const COLLECTION_RESPAWN_MS = 720;
const EXPLOSION_DURATION_MS = 1400;
const SECOND_EXPLOSION_DELAY_MS = 200;
const CANYON_CAMERA_LOOP_MS = 18000;
const CANYON_CAMERA_TRAVEL_DEPTH = 180;
const CANYON_CAMERA_PAN_X = 24;
const CANYON_CAMERA_PAN_Y = 8;
const CANYON_BASE_DEPTH = -2600;
const CANYON_BASE_SCALE = 3.4;
const CANYON_SECOND_PARALLAX_FACTOR = 0.42;
const CANYON_FOREGROUND_PARALLAX_FACTOR = 1;
const CANYON_SECOND_DEPTH = -1800;
const CANYON_SECOND_SCALE = 0.65;
const CANYON_BIRD_CYCLE_MS = 20000;
const CANYON_BIRD_FRAME_MS = 150;
const CANYON_BIRD_DEPTH = -2200;
const CANYON_BIRD_VERTICAL_SWAY = 6.5;
const CANYON_BIRD_FLOAT_CYCLES_PER_LOOP = 2;
const CANYON_BIRD_VERTICAL_JITTER = 8;
const CANYON_BIRD_JITTER_CYCLES_PER_LOOP = 3;
const CANYON_BIRD_COUNT = 3;
const CANYON_BIRD_OPPOSITE_INDEX = 2;
const CANYON_BIRD_CLONE_SCALE = 0.88;
const CANYON_BIRD_DELAY_MS = 1000;
const CANYON_BIRD_VERTICAL_GAP_PX = 50;
const CANYON_BIRD_VERTICAL_SCATTER_PX = 64;
const CANYON_BIRD_VERTICAL_OFFSET_PX = -100;
// The bird sits on a far 3D plane, so perspective compresses horizontal travel.
// These wider bounds let the full sprite clear both screen edges before it
// reverses direction.
const CANYON_BIRD_START_X = -120;
const CANYON_BIRD_END_X = 220;
const MOUSE_VERTICAL_MAX = 1.4;
const BALLOON_HORIZONTAL_TRAVEL_LEFT = 30;
const BALLOON_HORIZONTAL_TRAVEL_RIGHT = 48;
const EXPLOSION_SCALE = 2.2;
const SECOND_EXPLOSION_SCALE = 0.7;
const SECOND_EXPLOSION_ORIGIN = '48.94% 54.42%';

const copy = {
  title: 'Canyon run',
  subtitle: 'Fly forward, collect the coins',


  exit: 'Leave game',
  targetCircle: 'Canyon coin',
  revealedCircle: 'Collected coin',
  revealedBomb: 'Bomb',
  balloon: 'Hot-air balloon',
  level: 'LEVEL',
  levelDoneTitle: (level: GameLevel) => `Level ${level} done`,
  levelDoneBody: (nextLevel: GameLevel) => `You cleared this canyon run. Continue to level ${nextLevel}, or cancel here.`,
  continueLevel: (nextLevel: GameLevel) => `Continue to level ${nextLevel}`,
  cancel: 'Cancel',
  wonTitle: 'YOU WON!',
  wonBody: 'Congratulations! You cleared all three canyon levels.',
  lostTitle: 'The run is over',
  lostBody: (level: GameLevel) => `The canyon got the better of us this time. Restart Level ${level} and take a cleaner line.`,
  scoreResult: (score: number) => `${score} points scored`,
  retryLevel: (level: GameLevel) => `Retry Level ${level}`,
  playAgain: 'Start again',
  backToStart: 'Back to start',

};

type Point = { x: number; y: number };
type OrbKind = 'number' | 'bomb';
type OrbState = 'hidden' | 'revealed';
type GamePhase = 'playing' | 'levelComplete' | 'won' | 'lost';

type Orb = Point & {
  id: number;
  kind: OrbKind;
  question: boolean;
  spriteIndex: number;
  value: number;
  state: OrbState;
  visible: boolean;
  depth: number;
};

const randomCoinCount = (level: GameLevel): number => {
  const { coinCountMultiplier } = LEVEL_CONFIGS[level];
  const minCoins = Math.round(MIN_COINS * coinCountMultiplier);
  const maxCoins = Math.round(MAX_COINS * coinCountMultiplier);
  return minCoins + Math.floor(Math.random() * (maxCoins - minCoins + 1));
};

const clamp = (value: number, min: number, max: number): number =>
  Math.min(Math.max(value, min), max);

const distance = (a: Point, b: Point): number => Math.hypot(a.x - b.x, a.y - b.y);



const isOrbTouchingBalloon = (balloon: Point, orb: Point): boolean => {
  const normalizedX = (balloon.x - orb.x) / COLLECTION_RADIUS_X;
  const normalizedY = (balloon.y - orb.y) / COLLECTION_RADIUS_Y;
  return normalizedX * normalizedX + normalizedY * normalizedY <= 1;
};

const balloonBoardPoint = (position: Point): Point => ({
  x: 50 + position.x * (position.x >= 0 ? BALLOON_HORIZONTAL_TRAVEL_RIGHT : BALLOON_HORIZONTAL_TRAVEL_LEFT),
  y: 50 + position.y * 27,
});

const createPoint = (balloon: Point, occupied: Point[]): Point => {
  let bestPoint: Point = { x: 50, y: 16 };
  let bestClearance = -Infinity;

  const consider = (point: Point): boolean => {
    const balloonDistance = distance(point, balloon);
    const nearestTargetDistance = occupied.length
      ? Math.min(...occupied.map((other) => distance(point, other)))
      : Number.POSITIVE_INFINITY;
    const clearance = Math.min(balloonDistance, nearestTargetDistance);

    if (balloonDistance > MIN_COIN_DISTANCE && nearestTargetDistance > MIN_COIN_SEPARATION) {
      bestPoint = point;
      return true;
    }

    if (balloonDistance > MIN_COIN_DISTANCE && clearance > bestClearance) {
      bestPoint = point;
      bestClearance = clearance;
    }

    return false;
  };

  for (let attempt = 0; attempt < 400; attempt += 1) {
    if (
      consider({
        x: 18 + Math.random() * 64,
        y: 16 + Math.random() * 62,
      })
    ) {
      return bestPoint;
    }
  }

  for (let y = 16; y <= 78; y += 2) {
    for (let x = 18; x <= 82; x += 2) {
      if (consider({ x, y })) {
        return bestPoint;
      }
    }
  }

  return bestPoint;
};

const createOrb = (id: number, balloon: Point, occupied: Point[], level: GameLevel): Orb => {
  const point = createPoint(balloon, occupied);
  const { bombFrequencyDivider } = LEVEL_CONFIGS[level];
  const kind: OrbKind = Math.random() < BOMB_CHANCE / bombFrequencyDivider ? 'bomb' : 'number';
  const spriteIndex = Math.floor(Math.random() * 24);

  return {
    id,
    ...point,
    kind,
    question: kind === 'bomb' || Math.random() < 0.42,
    spriteIndex,
    value: kind === 'number' ? spriteIndex + 1 : 0,
    state: 'hidden',
    visible: true,
    depth: 0.18 + Math.random() * 0.82,
  };
};

const createInitialOrbs = (level: GameLevel): Orb[] => {
  const balloon = balloonBoardPoint({ x: 0, y: 0 });
  const coins: Orb[] = [];
  const coinCount = randomCoinCount(level);

  for (let index = 0; index < coinCount; index += 1) {
    coins.push(createOrb(index + 1, balloon, coins, level));
  }

  return coins;
};

const spriteAxisPosition = (center: number, cells: number): number =>
  ((center * cells / SPRITE_ATLAS_SIZE - 0.5) / (cells - 1)) * 100;

const spritePosition = (index: number): Pick<CSSProperties, 'backgroundPosition'> => {
  const column = index % SPRITE_COLUMNS;
  const row = Math.floor(index / SPRITE_COLUMNS) % SPRITE_ROWS;
  return {
    backgroundPosition: `${spriteAxisPosition(SPRITE_CENTER_X[column], SPRITE_COLUMNS)}% ${spriteAxisPosition(SPRITE_CENTER_Y[row], SPRITE_ROWS)}%`,
  };
};

const collectibleSpriteStyle = (url: string, index: number): CSSProperties => ({
  backgroundImage: `url("${url}")`,
  backgroundRepeat: 'no-repeat',
  backgroundSize: `${SPRITE_COLUMNS * 100}% ${SPRITE_ROWS * 100}%`,
  ...spritePosition(index),
});

const bombSpriteStyle = (exploded: boolean): CSSProperties => ({
  backgroundImage: `url("${GAME_IMAGES.bomb}")`,
  backgroundRepeat: 'no-repeat',
  backgroundSize: '200% 100%',
  backgroundPosition: exploded ? '100% 50%' : '0% 50%',
});

export const GameForFun: FC<A2uiNodeViewProps> = () => {
  const dispatch = useSurfaceAction();
  const gameRef = useRef<HTMLDivElement>(null);
  const boardRef = useRef<HTMLDivElement>(null);
  const canyonSceneRef = useRef<HTMLDivElement>(null);
  const canyonSecondRef = useRef<HTMLImageElement>(null);
  const canyonForegroundRef = useRef<HTMLImageElement>(null);
  const birdMotionRefs = useRef<Array<HTMLDivElement | null>>([]);
  const birdSpriteRefs = useRef<Array<HTMLDivElement | null>>([]);
  const balloonFloatRef = useRef<HTMLDivElement>(null);
  const coinFloatRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const positionRef = useRef<Point>({ x: 0, y: 0 });
  const mouseTargetRef = useRef<Point>({ x: 0, y: 0 });
  const mouseFrameRef = useRef<number | null>(null);
  const mouseActiveRef = useRef(false);
  const leanResetRef = useRef<number | null>(null);
  const respawnTimersRef = useRef<number[]>([]);
  const [position, setPosition] = useState<Point>({ x: 0, y: 0 });
  const [lean, setLean] = useState(0);
  const [level, setLevel] = useState<GameLevel>(1);
  const [collectedCoins, setCollectedCoins] = useState(0);
  const [score, setScore] = useState(0);
  const [lastCollectedValue, setLastCollectedValue] = useState<number | null>(null);
  const [bombs, setBombs] = useState(0);
  const [phase, setPhase] = useState<GamePhase>('playing');
  const [orbs, setOrbs] = useState<Orb[]>(() => createInitialOrbs(1));
  const [secondaryExplosionIds, setSecondaryExplosionIds] = useState<Set<number>>(() => new Set());

  const balloonPoint = balloonBoardPoint(position);
  const levelConfig = LEVEL_CONFIGS[level];


  useEffect(() => {
    if (phase === 'playing') {
      return;
    }

    mouseActiveRef.current = false;
    if (mouseFrameRef.current !== null) {
      window.cancelAnimationFrame(mouseFrameRef.current);
      mouseFrameRef.current = null;
    }
  }, [phase]);

  useEffect(() => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) {
      return undefined;
    }

    const birdVerticalScatter = Array.from(
      { length: CANYON_BIRD_COUNT },
      (_, birdIndex) => birdIndex === 0 ? 0 : Math.round((Math.random() * 2 - 1) * CANYON_BIRD_VERTICAL_SCATTER_PX),
    );
    const birdVerticalPhases = Array.from(
      { length: CANYON_BIRD_COUNT },
      () => Math.random() * Math.PI * 2,
    );
    const birdFrameCache = Array(CANYON_BIRD_COUNT).fill(-1);
    const birdDirectionCache: Array<boolean | null> = Array(CANYON_BIRD_COUNT).fill(null);
    let frame = 0;
    const startedAt = performance.now();
    const animate = (now: number): void => {
      const elapsed = now - startedAt;
      const cameraPhase = (elapsed % CANYON_CAMERA_LOOP_MS) / CANYON_CAMERA_LOOP_MS;
      const cameraProgress = (1 - Math.cos(cameraPhase * Math.PI * 2)) / 2;
      const cameraDepth = cameraProgress * CANYON_CAMERA_TRAVEL_DEPTH;
      const cameraPanX = cameraProgress * CANYON_CAMERA_PAN_X;
      const cameraPanY = cameraProgress * CANYON_CAMERA_PAN_Y;
      const secondLayerDepth = CANYON_SECOND_DEPTH + cameraDepth * CANYON_SECOND_PARALLAX_FACTOR;
      const foregroundDepth = cameraDepth * CANYON_FOREGROUND_PARALLAX_FACTOR;
      const floatOffset = Math.sin(elapsed / 1250) * 13;
      const swayOffset = Math.cos(elapsed / 1700) * 3;

      if (balloonFloatRef.current) {
        balloonFloatRef.current.style.transform = `translate3d(${swayOffset}px, ${floatOffset}px, 0)`;
      }
      for (const [coinId, coinElement] of coinFloatRefs.current) {
        const coinFloatY = Math.sin(elapsed / 900 + coinId * 0.73) * (8 + (coinId % 3) * 2);
        const coinFloatX = Math.cos(elapsed / 1100 + coinId * 0.51) * (3 + (coinId % 2) * 1.2);
        coinElement.style.setProperty('--coin-float-y', `${coinFloatY}px`);
        coinElement.style.setProperty('--coin-float-x', `${coinFloatX}px`);
      }
      if (canyonSecondRef.current) {
        canyonSecondRef.current.style.transform = `translate3d(${cameraPanX * CANYON_SECOND_PARALLAX_FACTOR}px, ${cameraPanY * CANYON_SECOND_PARALLAX_FACTOR}px, ${secondLayerDepth}px) scale(${CANYON_SECOND_SCALE})`;
      }
      if (canyonForegroundRef.current) {
        canyonForegroundRef.current.style.transform = `translate3d(${cameraPanX}px, ${cameraPanY}px, ${foregroundDepth}px) scale(1.04)`;
      }
      for (let birdIndex = 0; birdIndex < CANYON_BIRD_COUNT; birdIndex += 1) {
        const birdElapsed = Math.max(0, elapsed - birdIndex * CANYON_BIRD_DELAY_MS);
        const birdCycleElapsed = ((birdElapsed % CANYON_BIRD_CYCLE_MS) + CANYON_BIRD_CYCLE_MS) % CANYON_BIRD_CYCLE_MS;
        const birdCycleProgress = birdCycleElapsed / CANYON_BIRD_CYCLE_MS;
        const birdOutbound = birdCycleProgress < 0.5;
        const birdTravelProgress = birdOutbound ? birdCycleProgress * 2 : (birdCycleProgress - 0.5) * 2;
        const birdMovesOpposite = birdIndex === CANYON_BIRD_OPPOSITE_INDEX;
        const birdMovingRight = birdMovesOpposite ? !birdOutbound : birdOutbound;
        const birdPathProgress = birdMovingRight ? birdTravelProgress : 1 - birdTravelProgress;
        const birdX = CANYON_BIRD_START_X + (CANYON_BIRD_END_X - CANYON_BIRD_START_X) * birdPathProgress;
        const birdPhase = birdVerticalPhases[birdIndex] ?? 0;
        const birdY =
          Math.sin(birdCycleProgress * Math.PI * 2 * CANYON_BIRD_FLOAT_CYCLES_PER_LOOP + birdPhase) * CANYON_BIRD_VERTICAL_SWAY
          + Math.sin(birdCycleProgress * Math.PI * 2 * CANYON_BIRD_JITTER_CYCLES_PER_LOOP + birdPhase * 1.37) * CANYON_BIRD_VERTICAL_JITTER;
        const birdFrame = Math.floor(birdCycleElapsed / CANYON_BIRD_FRAME_MS) % 3;
        const birdMotionElement = birdMotionRefs.current[birdIndex];
        const birdSpriteElement = birdSpriteRefs.current[birdIndex];

        if (birdMotionElement) {
          const birdVerticalOffset = CANYON_BIRD_VERTICAL_OFFSET_PX - birdIndex * CANYON_BIRD_VERTICAL_GAP_PX + birdVerticalScatter[birdIndex];
          birdMotionElement.style.transform = `translate3d(${birdX}%, calc(${birdY}% + ${birdVerticalOffset}px), ${CANYON_BIRD_DEPTH}px)`;
        }
        if (birdSpriteElement) {
          const birdScale = birdIndex === 0 ? 1 : CANYON_BIRD_CLONE_SCALE;
          if (birdDirectionCache[birdIndex] !== birdMovingRight) {
            birdSpriteElement.style.transform = `scale(${birdScale}) scaleX(${birdMovingRight ? 1 : -1})`;
            birdDirectionCache[birdIndex] = birdMovingRight;
          }
          if (birdFrameCache[birdIndex] !== birdFrame) {
            birdSpriteElement.style.backgroundPosition = `${birdFrame * 50}% 50%`;
            birdFrameCache[birdIndex] = birdFrame;
          }
        }
      }

      frame = window.requestAnimationFrame(animate);
    };

    frame = window.requestAnimationFrame(animate);
    return () => window.cancelAnimationFrame(frame);
  }, []);


  useEffect(() => {
    if (phase !== 'playing') {
      return undefined;
    }

    const visibilityTimer = window.setInterval(() => {
      setOrbs((current) =>
        current.map((orb) => {
          if (orb.state !== 'hidden') {
            return orb;
          }

          const nextVisible = orb.visible
            ? Math.random() > 0.16
            : Math.random() < 0.48;
          return nextVisible === orb.visible ? orb : { ...orb, visible: nextVisible };
        }),
      );
    }, 2200);

    return () => window.clearInterval(visibilityTimer);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'playing') {
      return;
    }

    const hit = orbs.find(
      (orb) => orb.state === 'hidden' && orb.visible && isOrbTouchingBalloon(balloonPoint, orb),
    );
    if (!hit) {
      return;
    }

    const isBomb = hit.kind === 'bomb';
    const collectedValue = isBomb ? 0 : hit.value;
    const nextCollectedCoins = isBomb ? collectedCoins : collectedCoins + 1;
    const nextScore = isBomb ? score : score + collectedValue;
    const nextBombs = isBomb ? bombs + 1 : bombs;

    setOrbs((current) =>
      current.map((orb) => (orb.id === hit.id ? { ...orb, state: 'revealed' } : orb)),
    );

    if (isBomb) {
      const secondaryExplosionTimer = window.setTimeout(() => {
        setSecondaryExplosionIds((current) => {
          const next = new Set(current);
          next.add(hit.id);
          return next;
        });
      }, SECOND_EXPLOSION_DELAY_MS);
      respawnTimersRef.current.push(secondaryExplosionTimer);
      setBombs(nextBombs);
      if (nextBombs >= MAX_BOMBS) {
        setPhase('lost');
        return;
      }
    } else {
      setCollectedCoins(nextCollectedCoins);
      setLastCollectedValue(collectedValue);
      setScore(nextScore);
      if (nextCollectedCoins >= levelConfig.targetCoins) {
        setPhase(level === 3 ? 'won' : 'levelComplete');
        return;
      }
    }

    const respawnTimer = window.setTimeout(() => {
      setSecondaryExplosionIds((current) => {
        const next = new Set(current);
        next.delete(hit.id);
        return next;
      });
      setOrbs((current) =>
        current.map((orb) =>
          orb.id === hit.id
            ? createOrb(orb.id, balloonPoint, current.filter((other) => other.id !== hit.id), level)
            : orb,
        ),
      );
    }, isBomb ? EXPLOSION_DURATION_MS : COLLECTION_RESPAWN_MS);
    respawnTimersRef.current.push(respawnTimer);
  }, [balloonPoint, bombs, collectedCoins, level, levelConfig.targetCoins, orbs, phase, score]);

  useEffect(
    () => () => {
      if (leanResetRef.current !== null) {
        window.clearTimeout(leanResetRef.current);
      }
      if (mouseFrameRef.current !== null) {
        window.cancelAnimationFrame(mouseFrameRef.current);
      }
      for (const timer of respawnTimersRef.current) {
        window.clearTimeout(timer);
      }
    },
    [],
  );

  const startMouseSmoothing = (): void => {
    if (mouseFrameRef.current !== null || !mouseActiveRef.current) {
      return;
    }

    const tick = (): void => {
      if (!mouseActiveRef.current || phase !== 'playing') {
        mouseFrameRef.current = null;
        return;
      }

      const current = positionRef.current;
      const target = mouseTargetRef.current;
      const deltaX = target.x - current.x;
      const deltaY = target.y - current.y;
      const nextPosition = {
        x: current.x + deltaX * 0.075,
        y: current.y + deltaY * 0.075,
      };

      positionRef.current = nextPosition;
      setPosition(nextPosition);
      setLean(clamp(deltaX * 54 + deltaY * 18, -16, 16));

      if (Math.abs(deltaX) < 0.002 && Math.abs(deltaY) < 0.002) {
        positionRef.current = target;
        setPosition(target);
        setLean(0);
        mouseFrameRef.current = null;
        return;
      }

      mouseFrameRef.current = window.requestAnimationFrame(tick);
    };

    mouseFrameRef.current = window.requestAnimationFrame(tick);
  };

  const nudge = (dx: number, dy: number): void => {
    if (phase !== 'playing') {
      return;
    }

    mouseActiveRef.current = false;
    if (mouseFrameRef.current !== null) {
      window.cancelAnimationFrame(mouseFrameRef.current);
      mouseFrameRef.current = null;
    }

    const current = positionRef.current;
    const nextPosition = {
      x: clamp(current.x + dx, -1, 1),
      y: clamp(current.y + dy, -1, 1),
    };
    positionRef.current = nextPosition;
    setPosition(nextPosition);
    setLean(clamp(dx * 44, -10, 10));

    if (leanResetRef.current !== null) {
      window.clearTimeout(leanResetRef.current);
    }
    leanResetRef.current = window.setTimeout(() => setLean(0), 260);
  };

  const handleMouseMove = (event: MouseEvent<HTMLDivElement>): void => {
    if (phase !== 'playing') {
      return;
    }

    const board = boardRef.current;
    if (!board) {
      return;
    }

    const bounds = board.getBoundingClientRect();
    const horizontal = (event.clientX - bounds.left) / bounds.width;
    const vertical = (event.clientY - bounds.top) / bounds.height;
    const verticalScale = vertical >= 0.5 ? MOUSE_VERTICAL_MAX : 1;
    mouseTargetRef.current = {
      x: clamp(horizontal * 2 - 1, -1, 1),
      y: clamp(((vertical - 0.5) / 0.27) * verticalScale, -1, MOUSE_VERTICAL_MAX),
    };
    mouseActiveRef.current = true;
    startMouseSmoothing();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const movement: Record<string, Point> = {
      ArrowLeft: { x: -0.18, y: 0 },
      ArrowRight: { x: 0.18, y: 0 },
      ArrowUp: { x: 0, y: -0.18 },
      ArrowDown: { x: 0, y: 0.18 },
    };
    const direction = movement[event.key];
    if (!direction) {
      return;
    }

    event.preventDefault();
    nudge(direction.x, direction.y);
  };

  const startLevel = (nextLevel: GameLevel, resetScore: boolean): void => {
    for (const timer of respawnTimersRef.current) {
      window.clearTimeout(timer);
    }
    respawnTimersRef.current = [];
    mouseActiveRef.current = false;
    if (mouseFrameRef.current !== null) {
      window.cancelAnimationFrame(mouseFrameRef.current);
      mouseFrameRef.current = null;
    }
    positionRef.current = { x: 0, y: 0 };
    mouseTargetRef.current = { x: 0, y: 0 };
    setPosition({ x: 0, y: 0 });
    setLean(0);
    setCollectedCoins(0);
    if (resetScore) {
      setScore(0);
    }
    setLastCollectedValue(null);
    setBombs(0);
    setSecondaryExplosionIds(new Set());
    setLevel(nextLevel);
    setPhase('playing');
    setOrbs(createInitialOrbs(nextLevel));
    window.requestAnimationFrame(() => gameRef.current?.focus({ preventScroll: true }));
  };

  const retryCurrentLevel = (): void => {
    startLevel(level, false);
  };

  const resetToLevelOne = (): void => {
    startLevel(1, true);
  };

  const continueToNextLevel = (): void => {
    if (level === 3) {
      return;
    }
    const nextLevel: GameLevel = level === 1 ? 2 : 3;
    startLevel(nextLevel, false);
  };

  const panelStyle: CSSProperties = {
    backgroundImage: `url("${GAME_IMAGES.panel}")`,
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '100% 100%',
  };

  return (
    <div className="flex min-h-full w-full items-center justify-center">
      <div
        ref={gameRef}
      className="game-for-fun relative mx-auto w-full max-w-[1240px] overflow-hidden rounded-[22px] bg-background shadow-[0_30px_90px_-24px_hsl(var(--primary)/0.2),0_0_32px_hsl(var(--primary)/0.06)] ring-1 ring-border/35 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary"
      data-wayfarer-step="1"
      data-wayfarer-step-available-through="1"
      role="dialog"
      aria-modal="true"
      aria-label={copy.title}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      style={{ marginBlockStart: 'clamp(4rem, 18vw, 16rem)', transform: 'translateY(-100px)' }}
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 z-[80] h-[2px] bg-gradient-to-r from-transparent via-primary/75 to-transparent" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 z-[80] h-[2px] bg-gradient-to-r from-transparent via-primary/75 to-transparent" aria-hidden="true" />
      <div
        ref={boardRef}
        className="relative aspect-[16/9] min-h-[480px] w-full overflow-hidden bg-background sm:min-h-0"
        style={{ perspective: '1200px', transformStyle: 'preserve-3d' }}
        onMouseMove={handleMouseMove}
      >
        <div ref={canyonSceneRef} className="pointer-events-none absolute inset-0 z-0 will-change-transform" style={{ transformStyle: 'preserve-3d' }}>
          <div className="absolute inset-[-4%] z-0" style={{ transformStyle: 'preserve-3d' }}>
            <img
              src={GAME_IMAGES.third}
              alt=""
              aria-hidden="true"
              className="h-full w-full object-cover"
              style={{
                transform: `translate3d(0, 0, ${CANYON_BASE_DEPTH}px) scale(${CANYON_BASE_SCALE})`,
                transformOrigin: 'center center',
                opacity: 1,
              }}
            />
          </div>

          <div className="pointer-events-none absolute inset-0 z-[5]" style={{ transformStyle: 'preserve-3d' }} aria-hidden="true">
            {Array.from({ length: CANYON_BIRD_COUNT }, (_, birdIndex) => (
              <div
                key={`canyon-bird-${birdIndex}`}
                ref={(element) => {
                  birdMotionRefs.current[birdIndex] = element;
                }}
                className="absolute inset-0 will-change-transform"
                style={{
                  transform: `translate3d(38%, calc(${CANYON_BIRD_VERTICAL_OFFSET_PX - birdIndex * CANYON_BIRD_VERTICAL_GAP_PX}px), ${CANYON_BIRD_DEPTH}px)`,
                  transformStyle: 'preserve-3d',
                }}
              >
                <div
                  ref={(element) => {
                    birdSpriteRefs.current[birdIndex] = element;
                  }}
                  className="absolute left-0 top-[30%] h-[clamp(52px,7vw,94px)] w-[clamp(58px,8vw,108px)] bg-transparent bg-no-repeat will-change-transform"
                  style={{
                    backgroundImage: `url("${GAME_IMAGES.bird}")`,
                    backgroundSize: '300% 100%',
                    backgroundPosition: '0% 50%',
                    transform: `scale(${birdIndex === 0 ? 1 : CANYON_BIRD_CLONE_SCALE})`,
                    transformOrigin: 'center center',
                  }}
                />
              </div>
            ))}
          </div>

          <div className="absolute inset-[-130%] z-10" style={{ transformStyle: 'preserve-3d' }}>
            <img
              ref={canyonSecondRef}
              src={GAME_IMAGES.second}
              alt=""
              aria-hidden="true"
              className="h-full w-full object-cover"
              style={{
                transform: `translate3d(0, 0, ${CANYON_SECOND_DEPTH}px) scale(${CANYON_SECOND_SCALE})`,
                transformOrigin: 'center center',
                opacity: 1,
              }}
            />
          </div>

          <div className="absolute inset-[-10%] z-20" style={{ transformStyle: 'preserve-3d' }}>
            <img
              ref={canyonForegroundRef}
              src={GAME_IMAGES.first}
              alt=""
              aria-hidden="true"
              className="h-full w-full object-cover"
              style={{
                transform: 'translate3d(0, 0, 0) scale(1.04)',
                transformOrigin: 'center center',
              }}
            />
          </div>
        </div>

        <div
          className="pointer-events-none absolute inset-0 z-20 bg-gradient-to-b from-background/20 via-transparent to-background/80"
          aria-hidden="true"
        />

        <div className="absolute inset-x-4 top-4 z-50 flex items-start justify-between gap-4 text-white sm:inset-x-7 sm:top-7">
          <div className="min-w-0 flex-1 overflow-hidden drop-shadow-[0_2px_16px_hsl(var(--foreground)/0.65)]">
            <h1 className="truncate whitespace-nowrap text-xs font-bold tracking-tight text-white drop-shadow-[0_2px_12px_hsl(var(--foreground)/0.65)] sm:text-sm lg:text-base">
              {copy.subtitle}
            </h1>
          </div>
          <div className="pointer-events-auto flex shrink-0 items-center gap-[18px]">
            <span className="flex items-center whitespace-nowrap text-xs font-bold tracking-tight text-white drop-shadow-[0_2px_12px_hsl(var(--foreground)/0.65)] sm:text-sm lg:text-base">
              {copy.level} {level}
            </span>
            <button
              type="button"
              onClick={() => dispatch?.('exitGame', { reason: 'back' })}
              aria-label={copy.exit}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/35 bg-background/45 text-white backdrop-blur-sm transition hover:border-primary hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              <X size={20} weight="bold" aria-hidden="true" />
            </button>
          </div>
        </div>


        <div className="absolute inset-0 z-30" style={{ transformStyle: 'preserve-3d' }}>
          {orbs.map((orb) => {
            const revealedBomb = orb.state === 'revealed' && orb.kind === 'bomb';
            const revealedNumber = orb.state === 'revealed' && orb.kind === 'number';
            const revealed = revealedBomb || revealedNumber;
            const spriteStyle = revealedBomb
              ? bombSpriteStyle(true)
              : revealedNumber
                ? collectibleSpriteStyle(GAME_IMAGES.withNumber, orb.spriteIndex)
                : collectibleSpriteStyle(
                    orb.question ? GAME_IMAGES.questions : GAME_IMAGES.noNumbers,
                    orb.spriteIndex,
                  );
            const faded = orb.state === 'hidden' && !orb.visible;
            const secondaryExplosionVisible = secondaryExplosionIds.has(orb.id);

            const coinScale = (revealedBomb ? EXPLOSION_SCALE : 1) * (faded ? 0.78 : 1);
            const coinDepth = -160 + orb.depth * 320;
            const coinTransform = revealedNumber
              ? `translate3d(var(--coin-float-x, 0px), calc(-10rem + var(--coin-float-y, 0px)), ${coinDepth}px) scale(${coinScale})`
              : `translate3d(var(--coin-float-x, 0px), var(--coin-float-y, 0px), ${coinDepth}px) scale(${coinScale})`;

            return (
              <div
                key={orb.id}
                role="img"
                aria-hidden={faded}
                aria-label={orb.state === 'hidden' ? copy.targetCircle : orb.kind === 'bomb' ? copy.revealedBomb : copy.revealedCircle}
                className={`absolute -translate-x-1/2 -translate-y-1/2 drop-shadow-[0_8px_10px_hsl(var(--background)/0.72)] transition-[opacity,transform] duration-1000 ease-in-out motion-reduce:transition-none ${faded ? 'pointer-events-none scale-75 opacity-0' : revealed ? 'pointer-events-none opacity-0' : 'scale-100 opacity-100'} ${revealedBomb ? 'h-[clamp(52px,7vw,92px)] w-[clamp(52px,7vw,92px)]' : 'h-[clamp(52px,7vw,92px)] w-[clamp(44px,5.8vw,76px)]'}`}
                  style={{
                    left: `${orb.x}%`,
                    top: `${orb.y}%`,
                    zIndex: Math.round(10 + orb.depth * 30),
                    transformStyle: 'preserve-3d',
                    transitionDuration: `${revealedBomb ? EXPLOSION_DURATION_MS : COLLECTION_RESPAWN_MS}ms`,
                  }}
              >
                <div
                  ref={(element) => {
                    if (element) {
                      coinFloatRefs.current.set(orb.id, element);
                    } else {
                      coinFloatRefs.current.delete(orb.id);
                    }
                  }}
                  className="relative h-full w-full will-change-transform transition-[transform] duration-1000 ease-out motion-reduce:transition-none"
                  style={{
                    transform: coinTransform,
                    transitionDuration: `${revealedBomb ? EXPLOSION_DURATION_MS : COLLECTION_RESPAWN_MS}ms`,
                  }}
                >
                  <div
                    className="absolute inset-0 bg-transparent bg-no-repeat transition-opacity duration-1000 ease-out motion-reduce:transition-none"
                    style={{ ...spriteStyle, opacity: 1 }}
                  />
                  {revealedBomb ? (
                    <div
                      aria-hidden="true"
                      className="pointer-events-none absolute inset-0 bg-transparent bg-no-repeat transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none"
                      style={{
                        ...bombSpriteStyle(true),
                        mixBlendMode: 'screen',
                        opacity: secondaryExplosionVisible ? 1 : 0,
                        transform: `scale(${secondaryExplosionVisible ? SECOND_EXPLOSION_SCALE : 0.45})`,
                        transformOrigin: SECOND_EXPLOSION_ORIGIN,
                      }}
                    />
                  ) : null}
                  {revealedNumber ? (
                    <span
                      aria-hidden="true"
                      className="pointer-events-none absolute left-1/2 top-full h-20 w-3 -translate-x-1/2 rounded-full bg-gradient-to-b from-primary via-primary/70 to-transparent blur-[1px] drop-shadow-[0_0_7px_hsl(var(--primary)/0.65)] sm:w-4"
                    />
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>

        <div
          className="pointer-events-none absolute inset-0 z-40 will-change-transform motion-safe:transition-transform motion-safe:duration-300 motion-safe:ease-out motion-reduce:transition-none"
          style={{
            transform: `translate(${position.x * (position.x >= 0 ? BALLOON_HORIZONTAL_TRAVEL_RIGHT : BALLOON_HORIZONTAL_TRAVEL_LEFT)}%, ${position.y * 27}%)`,
          }}
          aria-hidden="true"
        >
          <div ref={balloonFloatRef} className="absolute inset-0 will-change-transform">
            <div
              className="absolute left-1/2 top-1/2 h-[clamp(147px,23.3vw,293px)] w-[clamp(147px,23.3vw,293px)] -translate-x-1/2 -translate-y-1/2 will-change-transform motion-safe:transition-transform motion-safe:duration-300 motion-safe:ease-out motion-reduce:transition-none"
              style={{ transform: `translate(-50%, -50%) rotate(${lean}deg)`, transformOrigin: '50% 72%' }}
            >
              <img src={GAME_IMAGES.balloon} alt={copy.balloon} className="h-full w-full object-contain drop-shadow-[0_20px_22px_hsl(var(--background)/0.45)]" />
            </div>
          </div>
        </div>


        <div className="pointer-events-none absolute bottom-4 left-1/2 z-50 grid w-[calc(100%-2rem)] max-w-[440px] -translate-x-1/2 gap-1.5 sm:grid-cols-2 sm:gap-2">
          <div className="relative flex min-h-[48px] items-center justify-center overflow-hidden rounded-[10px] p-2 text-white shadow-[0_5px_14px_hsl(var(--background)/0.55)] sm:min-h-[56px] sm:p-3" style={panelStyle}>
            <div className="absolute inset-0 bg-background/20" aria-hidden="true" />
            <div className="relative z-10 flex items-baseline justify-center gap-2" aria-live="polite">
              <p className="text-xl font-bold leading-none tabular-nums text-white sm:text-2xl">
                {collectedCoins}
                <span className="ml-1 text-sm font-semibold text-white/75 sm:text-base">/ {levelConfig.targetCoins}</span>
              </p>
              {lastCollectedValue !== null ? (
                <span className="text-sm font-bold tabular-nums text-white sm:text-base">+{lastCollectedValue}</span>
              ) : null}
            </div>
          </div>
          <div className="relative flex min-h-[48px] items-center justify-center overflow-hidden rounded-[10px] p-1 text-white shadow-[0_5px_14px_hsl(var(--background)/0.55)] sm:min-h-[56px] sm:p-1.5" style={panelStyle}>
            <div className="absolute inset-0 bg-background/20" aria-hidden="true" />
            <div className="relative z-10 flex items-center justify-center gap-0" aria-label="Bomb indicators" role="img">
              {Array.from({ length: BOMB_INDICATORS }, (_, index) => {
                const exploded = index < bombs;
                return (
                  <span
                    key={index}
                    className="h-10 w-10 bg-no-repeat contrast-[0.85] drop-shadow-[0_2px_4px_hsl(var(--background)/0.5)] sm:h-12 sm:w-12"
                    style={bombSpriteStyle(exploded)}
                    aria-hidden="true"
                  />
                );
              })}
            </div>
          </div>
        </div>


        {phase !== 'playing' ? (
          <div className="absolute inset-0 z-[70] flex items-center justify-center bg-black/60 p-5 sm:p-8">
            <div className="w-full max-w-md rounded-[22px] border border-primary/30 bg-card/95 p-6 text-center text-foreground shadow-2xl backdrop-blur-md sm:p-8">
              <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
                {phase === 'levelComplete' ? copy.levelDoneTitle(level) : phase === 'won' ? copy.wonTitle : copy.lostTitle}
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                {phase === 'levelComplete' ? copy.levelDoneBody(level === 1 ? 2 : 3) : phase === 'won' ? copy.wonBody : copy.lostBody(level)}
              </p>
              <p className="mt-5 text-lg font-bold tabular-nums text-foreground">{copy.scoreResult(score)}</p>
              {phase === 'levelComplete' ? (
                <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
                  <button
                    type="button"
                    onClick={continueToNextLevel}
                    className="rounded-full bg-primary px-5 py-3 text-sm font-bold text-primary-foreground transition hover:brightness-110 active:scale-[0.98] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                  >
                    {copy.continueLevel(level === 1 ? 2 : 3)}
                  </button>
                  <button
                    type="button"
                    onClick={() => dispatch?.('exitGame', { reason: 'back' })}
                    className="rounded-full border border-border bg-muted px-5 py-3 text-sm font-bold text-foreground transition hover:border-primary hover:text-primary active:scale-[0.98] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                  >
                    {copy.cancel}
                  </button>
                </div>
              ) : (
                <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
                  <button
                    type="button"
                    onClick={phase === 'lost' ? retryCurrentLevel : resetToLevelOne}
                    className="rounded-full bg-primary px-5 py-3 text-sm font-bold text-primary-foreground transition hover:brightness-110 active:scale-[0.98] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                  >
                    {phase === 'lost' ? copy.retryLevel(level) : copy.playAgain}
                  </button>
                  <button
                    type="button"
                    onClick={() => dispatch?.('exitGame', { reason: 'back' })}
                    className="rounded-full border border-border bg-muted text-foreground px-5 py-3 text-sm font-bold transition hover:border-primary hover:text-primary active:scale-[0.98] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                  >
                    {copy.backToStart}
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
    </div>
  );
};
